import { domParser } from "lib/defaultDomParser";
import { fetchSvgs } from "lib/svg/svg";
import { isHexString } from "lib/parseColor";

import {
  FILL_VALUE_REGEX,
  SVG_FILL_SELECTOR,
  SVG_SHAPE_SELECTOR
} from "lib/constants";

export const fillColors = svg => {
  const fillList = [];
  const elements = svg.querySelectorAll("[id^='change']");

  if (elements.length === 0) return [];

  elements.forEach(element => {
    const fills = element.querySelectorAll("[fill]");

    // if at least one fill colour is found set as default, otherwise set to black
    fillList.push({
      color: fills.length ? fills[0].attributes.fill.value : "#000",
      domId: element.id
    });
  });

  return fillList;
};

export const getClassDefinitionsFromStyleString = styleString => {
  // split by } to get the end of style blocks
  const styleBlocks = styleString.split("}");

  const classDefinitions = {};

  styleBlocks
    .filter(x => x)
    .forEach(styleBlock => {
      const classNameSections = styleBlock.split("{")[0];

      const classNames = classNameSections
        .split(",")
        .map(className => className.replace(".", "").trim());

      const bracketSplitBlock = styleBlock.split("{");
      const fillColorMatch =
        bracketSplitBlock.length > 1
          ? bracketSplitBlock[1].match(FILL_VALUE_REGEX)
          : null;

      // Only record classes that actually declare a fill. Non-hex values
      // (currentColor, var(...), rgba(...)) are kept too: they never produce a
      // recolour swatch (the swatch pass filters for hex), but they let the
      // implicit-black pass see that such a shape already has a fill and leave
      // it as-is instead of forcing it to black.
      if (!fillColorMatch || !fillColorMatch[1]) {
        return;
      }
      const classFillColor = fillColorMatch[1].trim();

      // we can add other attributes to this later for handling of stroke colors
      classNames.forEach(className => {
        classDefinitions[className] = {
          fill: classFillColor
        };
      });
    });

  return classDefinitions;
};

// Reads the fill an element declares for itself: the `fill` attribute, then an
// inline `style` fill, then a class fill resolved from the svg's <style> block.
// Returns the declared value (which may be "none") or null if none is declared.
const getElementFillDeclaration = (element, classDefinitions) => {
  const fillAttribute = element.getAttribute("fill");
  if (fillAttribute) return fillAttribute;

  const styleAttribute = element.getAttribute("style");
  const styleFillMatch = styleAttribute
    ? styleAttribute.match(FILL_VALUE_REGEX)
    : null;
  if (styleFillMatch && styleFillMatch[1]) return styleFillMatch[1].trim();

  const classAttribute = element.getAttribute("class");
  if (classAttribute) {
    // Intentional asymmetry: this resolver is permissive (multi-class split) so
    // inherited-fill detection treats `<path class="cls extra">` as having the
    // `.cls` fill. The class pass in getRecolorSections uses the exact-match
    // selector `*[class="${name}"]` and will not register that same node — its
    // own fill is therefore considered "already handled" here but never gets a
    // change id. Preserved from pre-PR behaviour; revisit if multi-class fills
    // start needing first-class swatches.
    const matched = classAttribute
      .split(/\s+/)
      .find(token => classDefinitions[token]);
    if (matched) return classDefinitions[matched].fill;
  }

  return null;
};

// SVG containers whose descendants exist only to be referenced (via <use>,
// clip-path, mask, etc.) and never render directly. Tagging a shape inside one
// as recolourable produces a phantom swatch, and stamping an `id="change_N"`
// onto a <defs> child silently breaks any <use href="#..."/> that points at it.
const NON_RENDERED_PARENTS = new Set([
  "defs",
  "clippath",
  "mask",
  "symbol",
  "pattern",
  "marker"
]);

const isInsideNonRenderedContext = node => {
  let parent = node.parentElement;
  while (parent && parent.tagName && parent.tagName.toLowerCase() !== "svg") {
    if (NON_RENDERED_PARENTS.has(parent.tagName.toLowerCase())) return true;
    parent = parent.parentElement;
  }
  return false;
};

// Walks node -> ancestors -> <svg> root and returns the first explicit fill
// declaration found (hex or "none"), or null when nothing in the chain declares
// a fill — meaning the node inherits the SVG default (black).
const resolveInheritedFill = (node, classDefinitions) => {
  let element = node;
  while (element) {
    const fill = getElementFillDeclaration(element, classDefinitions);
    if (fill !== null) return fill;
    if (element.tagName && element.tagName.toLowerCase() === "svg") break;
    element = element.parentElement;
  }
  return null;
};

// Single source of truth for recolourable sections, in the exact order their
// change ids are assigned. Consumed by both fillColorsFromUpload (swatch list)
// and setFillColorIds (DOM id stamping) so the two cannot drift.
export const getRecolorSections = svg => {
  const sections = [];
  const included = new Set();

  const svgStyleElement = svg.querySelector("style");
  const classDefinitions = svgStyleElement
    ? getClassDefinitionsFromStyleString(svgStyleElement.textContent)
    : {};

  Object.keys(classDefinitions).forEach(className => {
    if (!isHexString(classDefinitions[className].fill)) return;
    svg.querySelectorAll(`*[class="${className}"]`).forEach(node => {
      if (included.has(node)) return;
      included.add(node);
      sections.push({ node, color: classDefinitions[className].fill });
    });
  });

  svg.querySelectorAll(SVG_FILL_SELECTOR).forEach(node => {
    if (included.has(node)) return;
    const styleAttribute = node.getAttribute("style");
    const styleFillMatch = styleAttribute
      ? styleAttribute.match(FILL_VALUE_REGEX)
      : [];
    const styleFillValue =
      styleFillMatch && styleFillMatch[1] ? styleFillMatch[1].trim() : "";
    const fillValue = node.getAttribute("fill") || styleFillValue;
    if (isHexString(fillValue)) {
      included.add(node);
      sections.push({ node, color: fillValue });
    }
  });

  // Shapes relying on the implicit SVG default fill (black) — only when no
  // explicit fill is declared on the node or any ancestor. Skip shapes inside
  // non-rendering containers (<defs>, <clipPath>, <mask>, <symbol>, <pattern>,
  // <marker>) so we don't add phantom swatches or rename ids that <use>
  // references depend on.
  svg.querySelectorAll(SVG_SHAPE_SELECTOR).forEach(node => {
    if (included.has(node)) return;
    if (isInsideNonRenderedContext(node)) return;
    if (resolveInheritedFill(node, classDefinitions) !== null) return;
    included.add(node);
    sections.push({ node, color: "#000000" });
  });

  return sections;
};

export const fillColorsFromUpload = svg => {
  const elements = svg.querySelectorAll("[id^='change']");
  if (elements.length > 0) {
    // if the svg already has change sections we should just use those
    return fillColors(svg);
  }

  return getRecolorSections(svg).map((section, index) => ({
    color: section.color,
    domId: `change_${index + 1}`
  }));
};

export const resizableOptions = svg => {
  return {
    resizableX:
      (svg.querySelectorAll("g[id=L]").length === 1 &&
        svg.querySelectorAll("g[id=C]").length === 1 &&
        svg.querySelectorAll("g[id=R]").length === 1) ||
      svg.querySelectorAll("[id=REPEAT_X]").length,
    resizableY:
      (svg.querySelectorAll("g[id=T]").length === 1 &&
        svg.querySelectorAll("g[id=C]").length === 1 &&
        svg.querySelectorAll("g[id=B]").length === 1) ||
      svg.querySelectorAll("[id=REPEAT_Y]").length
  };
};

export const fetchSvgDataFromUrl = async url => {
  const [dataPromise] = await fetchSvgs([url]);

  return await dataPromise;
};

export const formattedDimension = dimension => {
  dimension.replace("px", "");
  return parseFloat(dimension);
};

const parseSvgToElement = async data => {
  const file = await fetchSvgDataFromUrl(data.src);

  if (!file) return null;

  const svg = domParser
    .parseFromString(file, "image/svg+xml")
    .querySelector("svg");

  return {
    ...resizableOptions(svg),
    type: "vector",
    id: data.id || data.mediaId,
    fillColors: fillColors(svg),
    name: data.name || "untitled",
    top: data.top || 0,
    left: data.left || 0,
    src: data.src,
    thumbSrc: data.thumbnailUrl || data.src,
    srcHeight: formattedDimension(svg.height.baseVal.valueAsString),
    srcWidth: formattedDimension(svg.width.baseVal.valueAsString),
    height: formattedDimension(svg.height.baseVal.valueAsString),
    width: formattedDimension(svg.width.baseVal.valueAsString)
  };
};

export default parseSvgToElement;
